import { Minus, Plus } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Markdown, type RenderOptions, titleHeadingIndex } from "@/components/markdown/Markdown";
import { parseMarkdown } from "@/components/markdown/parse";
import { Notice } from "@/components/Notice";
import { cn } from "@/lib/utils";
import { DiffView } from "./DiffView.tsx";
import { markedMarkdown } from "./marked.ts";

/** The reading view's three ways of showing a proposed page (wiki-collaboration-ux D1). */
export const READING_VIEWS = ["marked", "clean", "source"] as const;
export type ReadingView = (typeof READING_VIEWS)[number];

/**
 * What the reading view renders for `view`: the view actually shown (a new page has no Marked, a
 * too-large diff opens in Source), the Markdown rendered (null in Source), the struck ranges and
 * the omitted title heading. The review places its passages on the same text.
 */
export function shownPage(
  base: string | null,
  proposed: string,
  view: ReadingView,
  title?: string,
): {
  shown: ReadingView;
  tooLarge: boolean;
  marked: ReturnType<typeof markedMarkdown>;
  markdown: string | null;
  skip: { start: number; end: number }[];
  omit: number | null;
} {
  const isNew = !base;
  const marked = isNew ? null : markedMarkdown(base ?? "", proposed);
  const tooLarge = !isNew && marked === null;
  const shown: ReadingView = tooLarge ? "source" : isNew && view === "marked" ? "clean" : view;
  const markdown =
    shown === "source" ? null : shown === "marked" && marked ? marked.markdown : proposed;
  return {
    shown,
    tooLarge,
    marked,
    markdown,
    skip: shown === "marked" && marked ? marked.marks.filter((m) => m.kind === "del") : [],
    omit: markdown === null ? null : titleHeadingIndex(parseMarkdown(markdown), title),
  };
}

export const readingViewOf = (value: string | null | undefined): ReadingView | null =>
  (READING_VIEWS as readonly string[]).includes(value ?? "") ? (value as ReadingView) : null;

/**
 * The proposed page as it will read, its changes marked against the base version (D1): inserted
 * text tinted and underlined, removed text struck, each closed by a plus or minus icon and an
 * "inserted" or "removed" word for assistive technology. Clean shows the result; Source the line
 * diff. A page whose diff is too large opens in Source with a note. The diff runs in the browser,
 * through the safe Markdown renderer: nothing is rendered as HTML.
 */
export function MarkedPage(props: {
  /** The base version's Markdown; null or empty for a new page (Marked then reads as Clean). */
  base: string | null;
  proposed: string;
  baseVersion: number | null;
  /** Source's heading when the base is not a version (the text a reviewer decided on). */
  baseLabel?: string;
  view: ReadingView;
  onView: (view: ReadingView) => void;
  /** The page's title, shown above: its duplicate leading heading is omitted. */
  title?: string;
  /** Heading anchors for the selection popover's section (`headingPrefix`). */
  headingPrefix?: string;
  resolve?: RenderOptions["resolve"];
  openWikiLink?: RenderOptions["openWikiLink"];
  /** Rendered under the toggle, above the page (the item's facts). */
  children?: ReactNode;
  /** The review's markers after a raw offset of the shown Markdown (`shownPage`). */
  inlineAt?: ReadonlyMap<number, ReactNode>;
  /** The review's blocking lines after a top-level block of the shown Markdown. */
  blockAfter?: ReadonlyMap<number, ReactNode>;
}) {
  const { t } = useTranslation();
  const { base, proposed, view, onView } = props;
  const isNew = !base;
  const { marked, tooLarge, shown } = useMemo(
    () => shownPage(base, proposed, view, props.title),
    [base, proposed, view, props.title],
  );
  const views = READING_VIEWS.filter((v) => !(isNew && v === "marked"));
  const render = {
    headingPrefix: props.headingPrefix,
    resolve: props.resolve,
    inlineAt: props.inlineAt,
    blockAfter: props.blockAfter,
    ...(props.title && { omitTitleHeading: props.title }),
  };
  return (
    <div data-esi-content="wiki" className="flex min-w-0 flex-col gap-4">
      <fieldset
        data-selection-ignore=""
        className="flex w-fit items-center rounded-md border border-line p-0.5"
      >
        <legend className="sr-only">{t("wiki.reading.views")}</legend>
        {views.map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={shown === v}
            disabled={tooLarge && v !== "source"}
            onClick={() => onView(v)}
            className={cn(
              "rounded-[5px] px-2.5 py-1 text-xs font-medium text-fg-secondary transition-colors duration-(--duration-fast)",
              "hover:text-fg disabled:opacity-50 disabled:hover:text-fg-secondary",
              shown === v && "bg-surface-selected text-fg",
            )}
          >
            {t(`wiki.reading.view.${v}`)}
          </button>
        ))}
      </fieldset>
      {props.children}
      {tooLarge ? <Notice>{t("wiki.reading.tooLarge")}</Notice> : null}
      {shown === "source" ? (
        // Raw Markdown and removed lines are no passage of the page as it reads: no selection
        // comment or instruction is offered on them.
        <div data-selection-ignore="">
          {isNew ? (
            <pre className="whitespace-pre-wrap break-words font-mono text-[13px] text-fg">
              {proposed}
            </pre>
          ) : (
            <DiffView
              before={base ?? ""}
              after={proposed}
              version={props.baseVersion ?? 1}
              label={props.baseLabel}
            />
          )}
        </div>
      ) : shown === "marked" && marked ? (
        <Markdown
          markdown={marked.markdown}
          decorations={marked.marks}
          // Fences hold removed and added lines merged: never drawn as one diagram.
          drawDiagrams={false}
          renderDecoration={(kind, children, key, last) => (
            <ChangeMark key={key} kind={kind as "ins" | "del"} last={last}>
              {children}
            </ChangeMark>
          )}
          {...render}
          {...(props.openWikiLink && { openWikiLink: props.openWikiLink })}
        />
      ) : (
        <Markdown
          markdown={proposed}
          {...render}
          {...(props.openWikiLink && { openWikiLink: props.openWikiLink })}
        />
      )}
    </div>
  );
}

function ChangeMark(props: { kind: "ins" | "del"; last: boolean; children: ReactNode }) {
  const { t } = useTranslation();
  const Tag = props.kind;
  const Icon = props.kind === "ins" ? Plus : Minus;
  return (
    <Tag
      data-mark={props.kind}
      className={cn(
        "rounded-[3px] px-px decoration-1 underline-offset-[3px]",
        props.kind === "ins"
          ? "bg-success-bg text-fg underline decoration-success-fg/60"
          : "bg-danger-bg text-fg-secondary line-through decoration-danger-fg/70",
      )}
    >
      {props.children}
      {props.last ? (
        <>
          <Icon
            aria-hidden="true"
            strokeWidth={2.25}
            className={cn(
              "ml-0.5 inline size-3 align-[-1px]",
              props.kind === "ins" ? "text-success-fg" : "text-danger-fg",
            )}
          />
          <span className="sr-only">
            {` ${t(props.kind === "ins" ? "wiki.reading.inserted" : "wiki.reading.removed")}`}
          </span>
        </>
      ) : null}
    </Tag>
  );
}
